import { Card, Elevation, Icon } from '@blueprintjs/core';
import { IconNames } from '@blueprintjs/icons';
import classNames from 'classnames';
import cloneDeep from 'lodash/cloneDeep';
import * as React from 'react';
import { v4 as uuid } from 'uuid';
import { Text, Tooltip } from 'vet-bones/components';
import { alphaSort, isNotEqual } from 'vet-bones/utils';

import { withConfirmation } from 'src/components/withConfirmation';
import { getOverviewDrawerViewItemTestId } from 'src/constants/testIds';
import { useDiagram } from 'src/containers/ProjectDiagram/hooks/useDiagramValue';
import { ModelRule } from 'src/containers/ProjectDiagram/types/data';
import { commitDiagramModel } from 'src/containers/ProjectDiagram/utils/commitDiagramModel';
import { addRule } from 'src/containers/ProjectDiagram/utils/rules/addRule';
import { getNextRuleDuplicateName } from 'src/containers/ProjectDiagram/utils/rules/getNextRuleDuplicateName';
import { removeRule } from 'src/containers/ProjectDiagram/utils/rules/removeRule';
import { ProjectRuleOverviewMenu } from 'src/containers/ProjectRuleOverviewDrawer/ProjectRuleOverviewMenu';
import {
  prevProjectRulesByIdSelector,
  projectInvalidRulesByIdSelector,
  projectRulesByIdSelector,
} from 'src/selectors/projectRules';
import { uiSlice } from 'src/store/ui/ui.slice';
import * as copy from 'src/templates/copy';
import { useReduxDispatch } from 'src/utils/hooks/useReduxDispatch';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import { useRouterParams } from 'src/utils/hooks/useRouterParams';

export const ProjectRuleOverviewDrawerView: React.VFC = () => {
  const dispatch = useReduxDispatch();
  const diagram = useDiagram();

  const { projectId } = useRouterParams();

  const invalidRulesById = useReduxSelector(
    projectInvalidRulesByIdSelector(projectId)
  );
  const rulesById = useReduxSelector(projectRulesByIdSelector(projectId));
  const prevRulesById = useReduxSelector(
    prevProjectRulesByIdSelector(projectId)
  );

  const handleEdit = React.useCallback(
    (ruleId: string) => {
      dispatch(
        uiSlice.actions.toggledRuleDetailDrawer({ isOpen: true, ruleId })
      );
    },
    [dispatch]
  );

  const handleDuplicate = React.useCallback(
    (rule: ModelRule) => {
      if (!diagram) {
        return;
      }
      commitDiagramModel(
        diagram,
        addRule({
          ...cloneDeep(rule),
          id: uuid(),
          name: getNextRuleDuplicateName(rule.name, rulesById),
        })
      );
    },
    [diagram, rulesById]
  );

  const handleDelete = React.useCallback(
    (ruleId: string) => {
      if (!diagram) {
        return;
      }
      withConfirmation({
        message: copy.components.projectRuleOverviewDrawer.deleteConfirmation,
        onConfirmationSuccess: () => {
          commitDiagramModel(diagram, removeRule(ruleId));
        },
      });
    },
    [diagram]
  );

  const list = Object.values(rulesById)
    .sort(alphaSort(({ name }) => name))
    .sort((a, b) => {
      if (invalidRulesById[a.id] && !invalidRulesById[b.id]) {
        return -1;
      }

      if (!invalidRulesById[a.id] && invalidRulesById[b.id]) {
        return 1;
      }

      return 0;
    })
    .map((rule, idx) => (
      <Card
        key={rule.id}
        className={classNames(
          'sd-drawer-project-rule-overview__view__card',
          'sd-draft-highlight-candidate',
          {
            'sd-draft-highlight-candidate--highlighted':
              prevRulesById && isNotEqual(prevRulesById?.[rule.id], rule),
          }
        )}
        data-testid={getOverviewDrawerViewItemTestId(rule.id, idx)}
        elevation={Elevation.ONE}
        interactive
        onClick={() => {
          handleEdit(rule.id);
        }}
      >
        <Text ellipsize>{rule.name}</Text>
        {invalidRulesById[rule.id] ? (
          <Tooltip
            content={copy.components.projectRuleOverviewDrawer.invalidRule}
          >
            <Icon icon={IconNames.WARNING_SIGN} />
          </Tooltip>
        ) : null}
        <ProjectRuleOverviewMenu
          idx={idx}
          onDelete={() => {
            handleDelete(rule.id);
          }}
          onDuplicate={() => {
            handleDuplicate(rule);
          }}
          rule={rule}
        />
      </Card>
    ));

  return <div className="sd-drawer-project-rule-overview__view">{list}</div>;
};
